import { useState } from 'react';
import { Download, X, Copy, Check } from 'lucide-react';

interface Props {
  html: string;
  subject: string;
  onClose: () => void;
}

export default function ExportModal({ html, subject, onClose }: Props) {
  const [copied, setCopied] = useState(false);
  const [tab, setTab] = useState<'html' | 'instructions'>('html');
  const sizeKb = (new Blob([html]).size / 1024).toFixed(1);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(html);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = html;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const download = () => {
    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `penobscot-morning-${Date.now()}.html`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-6" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-3">
          <div className="w-8 h-8 rounded-md bg-emerald-50 border border-emerald-100 flex items-center justify-center">
            <Download size={14} className="text-emerald-700" />
          </div>
          <div className="flex-1">
            <div className="font-semibold text-slate-900 text-sm">Export to Resend</div>
            <div className="text-xs text-slate-500">{sizeKb} KB · ready to paste into a new Broadcast</div>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded text-slate-500"><X size={16} /></button>
        </div>

        <div className="px-5 pt-3 flex gap-1 border-b border-slate-100">
          <button onClick={() => setTab('html')} className={`px-3 py-1.5 text-xs font-medium rounded-t-md ${tab === 'html' ? 'bg-slate-50 text-slate-900 border-x border-t border-slate-200 -mb-px' : 'text-slate-500 hover:text-slate-700'}`}>HTML</button>
          <button onClick={() => setTab('instructions')} className={`px-3 py-1.5 text-xs font-medium rounded-t-md ${tab === 'instructions' ? 'bg-slate-50 text-slate-900 border-x border-t border-slate-200 -mb-px' : 'text-slate-500 hover:text-slate-700'}`}>Send via Resend</button>
        </div>

        <div className="flex-1 overflow-hidden flex flex-col">
          {tab === 'html' ? (
            <pre className="font-mono text-xs bg-slate-50 p-4 overflow-auto flex-1 text-slate-700 whitespace-pre-wrap break-all">{html}</pre>
          ) : (
            <div className="p-5 overflow-y-auto text-sm text-slate-700 space-y-3">
              <p>This file is a complete email-safe HTML document. Resend's Broadcast editor accepts custom HTML directly.</p>
              <ol className="space-y-2 list-decimal list-inside text-sm">
                <li>Open <span className="font-mono text-xs bg-slate-100 px-1 py-0.5 rounded">resend.com/broadcasts</span> and click <strong>Create broadcast</strong>.</li>
                <li>Choose your audience (e.g. Penobscot Morning subscribers).</li>
                <li>In the editor, click the <strong>{'{ }'}</strong> code icon and paste the HTML, or use the API:</li>
              </ol>
              <pre className="font-mono text-[11px] bg-slate-900 text-emerald-200 p-3 rounded-md overflow-x-auto">{`await resend.broadcasts.create({\n  audienceId: 'aud_xxxxxxxx',\n  from: 'Penobscot Morning <hello@penobscotmorning.com>',\n  subject: '${(subject || 'Your subject').replace(/'/g, "\\'")}',\n  html: \`...paste exported HTML...\`,\n});`}</pre>
              <p className="text-xs text-slate-500">The footer already includes <span className="font-mono text-[11px] bg-slate-100 px-1 py-0.5 rounded">{'{{{RESEND_UNSUBSCRIBE_URL}}}'}</span>, so unsubscribe handling is automatic.</p>
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-slate-200 flex items-center gap-2 bg-slate-50 rounded-b-xl">
          <div className="flex-1 text-xs text-slate-500">{copied ? '✓ Copied to clipboard' : `${html.split('\n').length} lines`}</div>
          <button onClick={download} className="px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-white border border-slate-200 rounded-md flex items-center gap-1.5">
            <Download size={13} /> Download .html
          </button>
          <button onClick={copy} className="px-3.5 py-1.5 text-sm font-semibold bg-blue-700 hover:bg-blue-800 text-white rounded-md flex items-center gap-1.5 shadow-sm">
            {copied ? <Check size={13} /> : <Copy size={13} />}
            {copied ? 'Copied' : 'Copy HTML'}
          </button>
        </div>
      </div>
    </div>
  );
}
